<script setup>
import { ref, nextTick } from 'vue';
import { useQuasar } from 'quasar';
import { FormChooser, ClientForm } from '@/components/admin';
import {
  useAuthStore,
  useSenderStore,
} from '@/stores';
import router from '@/router';
import Tr from '@/i18n/translation';
import { UserService } from '@/services';
import { Logger } from '../../middlewares/sentry';
import { useGKSStore } from '../../stores/gks/gks.store';
import { useBingoStore } from '@/stores/bingo/bingo.store';
import { useSaveDateStore } from '@/stores/saveDate/saveDate.store';
import { useEventStore } from '@/stores/event/event.store';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { useStateStore } from '@/stores/state.store';

const quasar = useQuasar();
const stateStore = useStateStore();
const authStore = useAuthStore();
const eventStore = useEventStore();
const rsvpStore = useRsvpStore();
const senderStore = useSenderStore();
const gksStore = useGKSStore();
const bingoStore = useBingoStore();
const saveDateStore = useSaveDateStore();

const clientId = ref('');

function isValidId(val) {
  const idPattern = /^([^A-Z]+)(.{2,})$/;
  return idPattern.test(val);
}

async function getData() {
  if (clientId.value && isValidId(clientId.value)) {
    await authStore
      .trySetupData(clientId.value)
      .then(() => {
        try {
          stateStore.trySetupData(clientId.value);
          eventStore.trySetupData(clientId.value);
          rsvpStore.trySetupData(clientId.value);
          gksStore.trySetupData(clientId.value);
          bingoStore.trySetupData(clientId.value);
          saveDateStore.trySetupData(clientId.value);
          quasar.notify({
            type: 'positive',
            message: `Pobrano dane klienta`,
          });
        } catch (err) {
          Logger.warn(err);
        }
      })
      .catch(() => {
        stateStore.resetData();
        quasar.notify({
          type: 'negative',
          message: `Klient nie istnieje`,
        });
      });
  }
}

async function openPage() {
  if (clientId.value) {
    await authStore
      .trySetupData(clientId.value)
      .then(() => {
        const routeData = router.resolve({
          name: 'Event',
          params: { id: clientId.value },
        });
        window.open(routeData.href, '_blank');
      })
      .catch(() => {
        stateStore.resetData();
        quasar.notify({
          type: 'negative',
          message: `Klient nie istnieje`,
        });
      });
  }
}

const isAccountCreationVisible = ref(false);

function openAccountForm() {
  nextTick(() => {
    setTimeout(() => {
      isAccountCreationVisible.value = true;
    }, 100);
  });
}

function handleFocusOutAccount() {
  isAccountCreationVisible.value = false;
}

async function sendAll() {
  try {
    await UserService.sendCommonData(
      clientId.value,
      stateStore.getCommonData.eventDate,
      stateStore.getCommonData.eventsType,
      stateStore.getCommonData.defaultType,
      stateStore.getCommonData.langList,
      stateStore.getCommonData.isNewSender,
    );

    await Promise.all([
      UserService.sendRsvpCommonData(
        clientId.value,
        rsvpStore.clientData.common,
      ),
      UserService.sendRsvpLangData(
        clientId.value,
        rsvpStore.clientData.lang[Tr.currentLocale],
        Tr.currentLocale,
      ),
      UserService.sendEventCommonData(
        clientId.value,
        eventStore.clientData.common,
      ),
      UserService.sendEventLangData(
        clientId.value,
        eventStore.clientData.lang[Tr.currentLocale],
        Tr.currentLocale,
      ),
    ]);

    for (const type in eventStore.getImages) {
      if (eventStore.getImages[type] instanceof Array) {
        let id = 0;
        for (const img of eventStore.getImages[type]) {
          const name = img.name.split('.');
          name.shift();

          const blob = await fetch(img.url)
            .then((response) => response.blob())
            .catch((error) => {
              Logger.warn(error);
            });

          const file = new File([blob], name.join('.'));
          senderStore.addImage(file, type, [{ id }, { lang: '' }]);
          id++;
        }
      } else {
        const name = eventStore.getImages[type].name.split('.');
        name.shift();

        const blob = await fetch(eventStore.getImages[type].url)
          .then((response) => response.blob())
          .catch((error) => {
            Logger.warn(error);
          });

        const file = new File([blob], name.join('.'));
        senderStore.addImage(file, type, [{ id: '0' }, { lang: '' }]);
      }
    }
    await senderStore.sendImages(
      'createSenderStream',
      'sendClientImage',
      clientId.value,
    );

    quasar.notify({
      type: 'info',
      message: `Wysłano dane klienta`,
    });
  } catch (err) {
    Logger.warn(err);
    quasar.notify({
      type: 'negative',
      message: `Błąd w przesyle danych`,
    });
  }
}
</script>

<template>
  <div class="input-client-id">
    <p class="label">ClientId</p>
    <q-input
      v-model="clientId"
      class="core-default-input-form"
      borderless
      autogrow
      lazy-rules
      :rules="[
        (val) => !!val || 'To pole nie może być puste',
        (val) => isValidId(val) || 'Błędny id',
      ]"
    />
    <div class="buttons">
      <button
        class="core-default-form-button"
        :disabled="clientId === ''"
        @click="getData"
      >
        Pobierz dane klienta
      </button>
      <button
        class="core-default-form-button"
        :disabled="clientId === ''"
        @click="openPage"
      >
        Otwórz stronę
      </button>
      <button
        class="core-default-form-button"
        :disabled="clientId === ''"
        @click="sendAll"
      >
        Wyślij wszystko
      </button>
      <button
        class="core-default-form-button"
        :disabled="clientId === ''"
        @click="openAccountForm"
      >
        Utwórz konto klienta
      </button>
    </div>
  </div>
  <div class="panel">
    <FormChooser :client-id="clientId" />
  </div>
  <transition name="fade" mode="out-in">
    <div v-show="isAccountCreationVisible" class="create-account">
      <ClientForm
        v-click-outside="handleFocusOutAccount"
        :client-id="clientId"
      />
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.panel {
  position: relative;
  width: 85%;
  margin: auto;
  display: flex;
  justify-content: space-evenly;
  gap: 25px;
  flex-wrap: wrap;
  padding-bottom: 60px;
}

.input-client-id {
  position: relative;
  width: 50%;
  min-width: 250px;
  margin: auto;
  padding: 50px 0 0;
  display: flex;
  flex-direction: column;
  gap: 25px;

  .label {
    text-align: center;
    margin: 0;
  }

  .buttons {
    display: flex;
    justify-content: space-evenly;
    gap: 15px;
    flex-wrap: wrap;
  }
}

.create-account {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #0008;
  z-index: 50;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
